import type { Query, QueryClient } from '@tanstack/react-query'

/**
 * Persistência do cache do react-query — política de privacidade (M-06 / TASK-2830).
 *
 * O cache é persistido em localStorage (sobrevive a F5). Como localStorage é
 * legível por qualquer script na origem e persiste após logout, NÃO podemos
 * gravar dados pessoais/sensíveis ali. Usamos uma ALLOWLIST explícita: só
 * queryKeys cujo primeiro segmento está em SAFE_QUERY_KEYS são persistidos.
 * Tudo mais (usuários, organizações, chat, finops, permission-templates, etc.)
 * fica apenas em memória e é refeito por fetch quando necessário.
 */
export const QUERY_CACHE_KEY = 'consoleai-query-cache'

/**
 * Allowlist de queryKeys[0] seguros para persistir. Deliberadamente mínima e
 * restrita a catálogos GLOBAIS não-PII (metadados do sistema, sem dados de
 * usuário/organização/tenant). Adicionar uma chave aqui exige confirmar que o
 * payload não contém dado pessoal nem sensível.
 *
 * - `permissions-catalog`: catálogo global de definições de permissão
 *   (recurso/ação/descrição). Metadado estático do sistema, sem PII.
 */
export const SAFE_QUERY_KEYS: ReadonlySet<string> = new Set<string>([
  'permissions-catalog',
])

/**
 * Persistir apenas queries bem-sucedidas cujo primeiro segmento da queryKey
 * está na allowlist. Espelha o comportamento padrão (só `success`) e adiciona
 * o filtro anti-PII.
 */
export function shouldDehydrateQuery(query: Query): boolean {
  if (query.state.status !== 'success') return false
  return SAFE_QUERY_KEYS.has(String(query.queryKey[0]))
}

// Instância registrada em main.tsx (evita dependência circular: os paths de
// logout — store/auth.ts e api/client.ts — importam só este módulo).
let _queryClient: QueryClient | null = null

export function registerQueryClient(queryClient: QueryClient): void {
  _queryClient = queryClient
}

/**
 * Limpeza total no logout: remove o cache em memória (queryClient.clear) e o
 * snapshot persistido em localStorage. Best-effort — nunca lança (não pode
 * bloquear o logout). Chamado nos DOIS paths de logout (voluntário e 401).
 */
export function clearPersistedQueryCache(): void {
  try {
    _queryClient?.clear()
  } catch {
    /* best-effort */
  }
  try {
    window.localStorage.removeItem(QUERY_CACHE_KEY)
  } catch {
    /* best-effort */
  }
}
